import { useCentralReady } from "../../../context/CentralReadyContext";
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { CacheRefreshService, CacheRefreshStatus, canRefreshPublishedPages } from "../../../services/CacheRefreshService";
import { SiteSwitcher } from "../../../services/SiteSwitcher";
import { UserType } from "../../../config/Enums";
import { EventBus } from "../../../helpers/EventBus";
import { actionEmitter, EmitEvent } from "../../../config/ActionEmitter";

interface CacheRefreshContextValue {
  canRefresh: boolean;
  busy: boolean;
  message: string;
  refresh: () => Promise<void>;
  reload: () => void;
}

const CacheRefreshContext = createContext<CacheRefreshContextValue>({
  canRefresh: false, busy: false, message: "Checking published pages…", refresh: async () => {}, reload: () => {}
});

export const useCacheRefresh = () => useContext(CacheRefreshContext);

export const CacheRefreshProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const sessionReady = useCentralReady();
  const [service] = useState(() => new CacheRefreshService());
  const [switcher] = useState(() => new SiteSwitcher());
  const [status, setStatus] = useState<CacheRefreshStatus | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const selected = useRef<string | null>(null);
  const allowed = useRef(false);
  const mounted = useRef(false);
  const requestId = useRef(0);
  const submittingRef = useRef(false);
  const follow = useRef(false);
  follow.current = (status?.Pending ?? 0) > 0 || !!status?.Active;

  const loadStatus = useCallback(async () => {
    const siteId = selected.current;
    if (!siteId || !allowed.current) return;
    const request = ++requestId.current;
    try {
      const result = await service.status(siteId);
      if (mounted.current && request === requestId.current && selected.current === siteId) {
        setStatus(result);
        setError(result ? "" : "Unable to check cache refresh status.");
      }
    } catch {
      if (mounted.current && request === requestId.current && selected.current === siteId) {
        setStatus(null);
        setError("Unable to check cache refresh status.");
      }
    }
  }, [service]);

  useEffect(() => {
    if (!sessionReady) return;
    mounted.current = true;
    let selectionGeneration = 0;
    const select = async () => {
      const generation = ++selectionGeneration;
      selected.current = switcher.getSelectedSiteKey();
      const siteId = selected.current;
      allowed.current = false;
      ++requestId.current;
      setStatus(null);
      setError("");
      try {
        const sites = await switcher.getAvailableSites();
        if (!mounted.current || generation !== selectionGeneration) return;
        const role = sites.find(s => s.SiteId === siteId)?.UserType;
        allowed.current = role === UserType.SiteOwner || role === UserType.SiteAdmin || role === UserType.SitePublisher;
        if (allowed.current) await loadStatus();
        else setError("Cache refresh is available to site owners, admins and publishers.");
      } catch {
        if (mounted.current && generation === selectionGeneration) setError("Unable to check cache refresh permissions.");
      }
    };
    const onResult = (success: boolean) => { if (success) void loadStatus(); };
    const bus = EventBus.getInstance();
    bus.subscribe(EventBus.Event.OnSiteSelected, select);
    actionEmitter.on(EmitEvent.Result, onResult);
    window.addEventListener("cms-publication-changed", loadStatus);
    const timer = window.setInterval(() => { if (!document.hidden && follow.current) void loadStatus(); }, 2000);
    const slow = window.setInterval(() => { if (!document.hidden && !follow.current) void loadStatus(); }, 10000);
    void select();
    return () => {
      mounted.current = false;
      ++selectionGeneration;
      ++requestId.current;
      bus.unsubscribe(EventBus.Event.OnSiteSelected, select);
      actionEmitter.off(EmitEvent.Result, onResult);
      window.removeEventListener("cms-publication-changed", loadStatus);
      window.clearInterval(timer);
      window.clearInterval(slow);
    };
  }, [loadStatus, switcher, sessionReady]);

  const refresh = async () => {
    const siteId = selected.current;
    if (!siteId || !allowed.current || submittingRef.current || !canRefreshPublishedPages(status, submitting)) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const result = await service.refresh(siteId);
      if (mounted.current && siteId === selected.current) {
        if (result?.Status === "queued") {
          setStatus(current => current ? { ...current, Pending: Math.max(current.Pending, 1), Active: true } : current);
          actionEmitter.emit(EmitEvent.Result, true, result.Message);
        } else {
          setError("The cache refresh was not accepted. Please try again.");
        }
      }
    } catch {
      if (mounted.current && siteId === selected.current) setError("Unable to queue the cache refresh. Please try again.");
    } finally {
      submittingRef.current = false;
      if (mounted.current) { setSubmitting(false); void loadStatus(); }
    }
  };

  const message = error || (!status ? "Checking published pages…" :
    !status.Enabled ? "Cache refresh is not configured." :
    status.Active ? "Refreshing published pages. You can continue editing." :
    status.Pending > 0 ? "A cache refresh is waiting to retry. You can continue editing." :
    status.Failed > 0 ? "Some cache refreshes failed. Refresh published pages to retry." :
    status.PublishedPageCount === 0 ? "Publish a page to enable cache refresh." :
    status.LastCompletedUtc ? `Cache refresh completed ${new Date(status.LastCompletedUtc).toLocaleString()}.` :
    `${status.PublishedPageCount} published pages across all languages.`);

  return <CacheRefreshContext.Provider value={{ canRefresh: canRefreshPublishedPages(status, submitting),
    busy: submitting || !!status?.Active, message, refresh, reload: () => { void loadStatus(); } }}>
    {children}
  </CacheRefreshContext.Provider>;
};
